import { Link, Outlet, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LayoutGrid, Key, CreditCard, User, LogOut, Menu, X, ShieldCheck } from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { useStaffRole } from "@/features/staff/hooks/useStaff";
import { useState } from "react";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { LanguageToggle } from "@/components/ui/LanguageToggle";

type NavLabelKey = "nav.apps" | "nav.apiKeys" | "nav.billing" | "nav.account" | "nav.staff";

interface NavItem {
  to: string;
  labelKey: NavLabelKey;
  icon: typeof LayoutGrid;
}

const navItems: NavItem[] = [
  { to: "/apps", labelKey: "nav.apps", icon: LayoutGrid },
  { to: "/api-keys", labelKey: "nav.apiKeys", icon: Key },
  { to: "/billing", labelKey: "nav.billing", icon: CreditCard },
  { to: "/account", labelKey: "nav.account", icon: User },
];

const staffNavItem: NavItem = { to: "/staff", labelKey: "nav.staff", icon: ShieldCheck };

export function DashboardShell() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { role: staffRole } = useStaffRole();
  const items = staffRole ? [...navItems, staffNavItem] : navItems;

  const handleLogout = () => {
    logout();
    navigate({ to: "/login" });
  };

  return (
    <div className="fixed inset-0 flex bg-void">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex w-64 flex-col overflow-y-auto border-r border-border bg-surface
          transition-transform duration-200 lg:static lg:translate-x-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex h-16 items-center gap-3 border-b border-border px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo to-cyan">
            <span className="text-sm font-bold text-white">A</span>
          </div>
          <span className="text-lg font-bold tracking-tight text-heading">
            {t("common.appName")}
          </span>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-muted hover:bg-elevated hover:text-heading lg:hidden"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {items.map((item) => {
            const isActive = matchRoute({ to: item.to, fuzzy: true });
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium
                  transition-colors duration-150
                  ${
                    isActive
                      ? "bg-indigo/10 text-indigo"
                      : "text-muted hover:bg-elevated hover:text-heading"
                  }
                `}
              >
                <Icon size={18} />
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-border p-3">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted transition-colors hover:bg-elevated hover:text-danger"
          >
            <LogOut size={18} />
            {t("nav.logout")}
          </button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-border bg-surface px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted hover:bg-elevated hover:text-heading lg:hidden"
          >
            <Menu size={20} />
          </button>

          <div className="hidden lg:block" />

          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageToggle />
          </div>
        </header>

        <main className="codes-warning-clearance flex-1 overflow-y-auto p-4 outline-none lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
